import React from "react";
import { Eye, EyeOff, Loader2, Pencil } from "lucide-react";
import { cn } from "@/lib/utils";
import { formatCurrency } from "@/lib/format";
import { getIngredientIcon, getPrice, isVisible } from "./ingredientUtils";

/** Um ingrediente do catálogo: ícone, nome, status, ID, preço e ações */
function IngredientCard({ ingredient, editing, toggling, onEdit, onToggleVisible }) {
  const visible = isVisible(ingredient);
  const price = getPrice(ingredient);
  const icon = getIngredientIcon(ingredient.name);
  const name = ingredient.name || "Sem nome";

  return (
    <article
      className={cn(
        "relative rounded-2xl border bg-card p-3.5 shadow-soft transition-all",
        editing
          ? "border-2 border-primary/60 shadow-md"
          : "border-border hover:border-brand-pistachio/60",
        !visible && !editing && "bg-surface-low"
      )}
    >
      {editing && (
        <span className="absolute -top-2.5 right-4 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground shadow-sm">
          Em edição
        </span>
      )}

      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <div
            className={cn(
              "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl text-lg",
              visible ? "bg-surface-container" : "bg-muted grayscale",
              !icon && "font-display text-sm font-bold text-primary"
            )}
            aria-hidden="true"
          >
            {icon || name.charAt(0).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h3
              className={cn(
                "truncate text-sm font-bold leading-tight",
                visible ? "text-foreground" : "text-muted-foreground"
              )}
              title={name}
            >
              {name}
            </h3>
            <div className="mt-0.5 flex items-center gap-1.5">
              <span
                className={cn(
                  "inline-flex items-center rounded border px-1.5 text-[10px] font-medium",
                  visible
                    ? "border-brand-pistachio/50 bg-secondary text-secondary-foreground"
                    : "border-terra/30 bg-terra-soft text-terra"
                )}
              >
                {visible ? "Visível" : "Oculto"}
              </span>
              <span className="font-mono text-[11px] text-muted-foreground">ID: {ingredient.id}</span>
            </div>
          </div>
        </div>
        <div className="flex-shrink-0 text-right">
          <span className="block text-[10px] font-semibold uppercase text-muted-foreground">Preço</span>
          <span
            className={cn(
              "font-display text-base font-bold",
              price === 0 ? "text-muted-foreground" : editing ? "text-primary" : "text-foreground"
            )}
          >
            {formatCurrency(price)}
          </span>
        </div>
      </div>

      <div className="mt-3 flex items-center justify-end gap-2 border-t border-border/60 pt-2.5">
        <button
          type="button"
          onClick={() => onEdit(ingredient)}
          className={cn(
            "flex h-8 items-center gap-1 rounded-full border px-3 text-xs transition-colors",
            editing
              ? "border-brand-pistachio/60 bg-secondary font-semibold text-primary"
              : "border-border bg-surface-container font-medium text-foreground hover:bg-surface-high"
          )}
        >
          <Pencil className="h-3.5 w-3.5" />
          {editing ? "Editando..." : "Editar"}
        </button>
        <button
          type="button"
          onClick={() => onToggleVisible(ingredient)}
          disabled={toggling}
          aria-label={`${visible ? "Ocultar" : "Exibir"} ${name}`}
          className={cn(
            "flex h-8 items-center gap-1 rounded-full border px-3 text-xs font-medium transition-colors disabled:opacity-60",
            visible
              ? "border-destructive/25 bg-destructive/5 text-destructive hover:bg-destructive/10"
              : "border-brand-pistachio/60 bg-secondary text-primary hover:bg-secondary/70"
          )}
        >
          {toggling ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : visible ? (
            <EyeOff className="h-3.5 w-3.5" />
          ) : (
            <Eye className="h-3.5 w-3.5" />
          )}
          {visible ? "Ocultar" : "Exibir"}
        </button>
      </div>
    </article>
  );
}

export default React.memo(IngredientCard);
